'use client';
import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/components/language/LanguageProvider';

interface StyleVisual {
  icon: React.ReactNode;
}

const STYLE_VISUALS: StyleVisual[] = [
  {
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M20 21a8 8 0 1 0-16 0" />
      </svg>
    ),
  },
  {
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    ),
  },
  {
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2" />
      </svg>
    ),
  },
  {
    icon: (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 20h4l10.5-10.5a2.83 2.83 0 0 0-4-4L4 16Z" />
        <path d="m14.5 5.5 4 4" />
      </svg>
    ),
  },
];

export default function AboutSection() {
  const revealRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { dictionary } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="relative bg-[#0A0A0A] py-24 overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #C8A96E 1px, transparent 1px), linear-gradient(to bottom, #C8A96E 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-10">
          {/* About text */}
          <div
            ref={(el) => {
              revealRefs.current[1] = el;
            }}
            className="reveal"
          >
            <span className="section-label">{dictionary.about.sectionLabel}</span>
            <h2
              className="text-4xl md:text-5xl font-black text-foreground tracking-tight leading-[1.05] mb-6"
              style={{ fontFamily: 'var(--font-fraunces)' }}
            >
              {dictionary.about.titleLine1}
              <br />
              <span className="text-gradient-gold italic">{dictionary.about.titleLine2}</span>
            </h2>
            <p className="text-muted-foreground text-base leading-[1.8] mb-4 max-w-2xl">
              {dictionary.about.paragraph1}
            </p>
            <p className="text-muted-foreground text-base leading-[1.8] max-w-2xl">
              {dictionary.about.paragraph2}
            </p>
          </div>

          {/* Style specialties — single row */}
          <div
            ref={(el) => {
              revealRefs.current[2] = el;
            }}
            className="reveal reveal-delay-1"
            id="styles"
          >
            <span className="section-label">{dictionary.about.specialties}</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {dictionary.about.specialtiesList.map((style, index) => (
                <div
                  key={style.name}
                  ref={(el) => {
                    revealRefs.current[3 + index] = el;
                  }}
                  className={`reveal reveal-delay-${index + 1} group bg-card border border-border hover:border-primary/35 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.4)] cursor-default`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary flex-shrink-0 group-hover:bg-primary/18 transition-colors">
                      {STYLE_VISUALS[index]?.icon}
                    </div>
                    <div>
                      <h3 className="text-foreground font-bold text-sm mb-1 group-hover:text-primary transition-colors duration-200">
                        {style.name}
                      </h3>
                      <p className="text-muted-foreground text-xs leading-[1.65]">
                        {style.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Instagram link immediately below styles */}
            <div className="mt-4">
              <a
                href="https://www.instagram.com/juga_tattoo/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:gap-3 transition-all duration-300 group"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-primary"
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
                </svg>
                {dictionary.about.instagram}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  className="group-hover:translate-x-1 transition-transform duration-300"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>

          {/* CTA */}
          <div
            ref={(el) => {
              revealRefs.current[7] = el;
            }}
            className="reveal reveal-delay-2"
          >
            <a
              href="#booking"
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 rounded-full font-bold text-sm hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(200,169,110,0.35)] active:translate-y-0"
            >
              {dictionary.about.cta}
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="group-hover:translate-x-1 transition-transform duration-300"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
